/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef, type ReactNode } from "react";
import {
	motion,
	useAnimation,
	useInView,
	useReducedMotion,
} from "motion/react";
import { useTranslation } from "react-i18next";
import DotGrid from "./DotGrid";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";

type IconName = "expertise" | "global" | "certified" | "manufacturing";

// Order is fixed; titles/descriptions resolve from the active locale.
const featureIcons: IconName[] = [
	"expertise",
	"global",
	"certified",
	"manufacturing",
];

const Icons: Record<IconName, ReactNode> = {
	expertise: (
		<svg
			className="h-6 w-6"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round">
			<circle
				cx="12"
				cy="12"
				r="10"
			/>
			<line
				x1="12"
				y1="16"
				x2="12"
				y2="12"
			/>
			<line
				x1="12"
				y1="8"
				x2="12.01"
				y2="8"
			/>
		</svg>
	),
	global: (
		<svg
			className="h-6 w-6"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round">
			<circle
				cx="12"
				cy="12"
				r="10"
			/>
			<path d="M2 12h20" />
			<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
		</svg>
	),
	certified: (
		<svg
			className="h-6 w-6"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round">
			<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
			<polyline points="9 12 11 14 15 10" />
		</svg>
	),
	manufacturing: (
		<svg
			className="h-6 w-6"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round">
			<circle
				cx="12"
				cy="12"
				r="3"
			/>
			<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
		</svg>
	),
};

export default function WhyChooseUs() {
	const { t } = useTranslation();
	const controls = useAnimation();
	const ref = useRef<HTMLElement | null>(null);
	const shouldReduceMotion = useReducedMotion();
	const isInView = useInView(ref, { once: true, amount: 0.2 });
	useGsapScrollParallax(ref, { y: -90, opacity: 0.92, scale: 0.985 });

	// Localized hero copy + feature list (kept in icon order).
	const heroTitle = t("whyChooseUs.title");
	const heroSubtitle = t("whyChooseUs.subtitle");
	const heroDescription = t("whyChooseUs.description");
	const features = featureIcons.map((icon) => ({
		icon,
		title: t(`whyChooseUs.features.${icon}.title`),
		description: t(`whyChooseUs.features.${icon}.description`),
	}));

	useEffect(() => {
		if (isInView) {
			controls.start("visible");
		}
	}, [controls, isInView]);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: shouldReduceMotion ? 0 : 0.14,
				delayChildren: 0.16,
			},
		},
	};

	const headerVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 24 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
		},
	};

	const cardVariants = {
		hidden: {
			opacity: 0,
			y: shouldReduceMotion ? 0 : 30,
			scale: shouldReduceMotion ? 1 : 0.97,
		},
		visible: (index: number) => ({
			opacity: 1,
			y: 0,
			scale: 1,
			transition: {
				duration: 0.65,
				delay: shouldReduceMotion ? 0 : index * 0.08,
				ease: [0.22, 1, 0.36, 1],
			},
		}),
	};

	return (
		<motion.section
			ref={ref}
			initial="hidden"
			animate={controls}
			variants={containerVariants}
			className="site-section relative bg-[#3b0092] overflow-hidden">
			{/* DotGrid Background - Fixed Positioning */}
			<div className="absolute inset-0 z-0">
				<DotGrid
					dotSize={4}
					gap={15}
					baseColor="#2c016c"
					activeColor="#f09a38"
					proximity={100}
					shockRadius={200}
					shockStrength={3}
					resistance={750}
					returnDuration={1.5}
				/>
			</div>

			{/* Animated Background Blobs */}
			<div className="absolute inset-0 z-0 pointer-events-none">
				<motion.div
					aria-hidden
					animate={
						shouldReduceMotion ?
							{}
						:	{
								x: [0, 20, 0],
								y: [0, -25, 0],
							}
					}
					transition={{
						duration: 9,
						repeat: Number.POSITIVE_INFINITY,
						ease: "easeInOut",
					}}
					className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
				/>
				<motion.div
					aria-hidden
					animate={
						shouldReduceMotion ?
							{}
						:	{
								x: [0, -20, 0],
								y: [0, 18, 0],
							}
					}
					transition={{
						duration: 11,
						repeat: Number.POSITIVE_INFINITY,
						ease: "easeInOut",
					}}
					className="absolute -bottom-16 right-0 h-80 w-80 rounded-full bg-blue-400/10 blur-3xl"
				/>
			</div>

			{/* Main Content - Higher z-index */}
			<div className="relative z-10 site-container px-4 sm:px-6 md:px-8 lg:px-12 mx-auto max-w-7xl w-full">
				<motion.div
					variants={headerVariants}
					className="mx-auto mb-10 sm:mb-12 md:mb-14 lg:mb-20 max-w-3xl text-center">
					<h2 className="mb-3 sm:mb-4 md:mb-5 text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white font-display leading-tight">
						{heroTitle}
					</h2>

					<p className="text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed text-white/80 px-2 sm:px-0">
						<span className="mb-2 block font-medium text-white/90 text-sm sm:text-base">
							{heroSubtitle}
						</span>
						{heroDescription}
					</p>
				</motion.div>

				<motion.div
					variants={containerVariants}
					className="grid gap-4 sm:gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
					{features.map((feature, index) => (
						<motion.article
							key={feature.title}
							custom={index}
							variants={cardVariants}
							whileHover={
								shouldReduceMotion ?
									{}
								:	{
										y: -10,
										scale: 1.02,
									}
							}
							transition={{ duration: 0.35, ease: "easeOut" }}
							className="group relative h-full rounded-[16px] sm:rounded-[20px] md:rounded-[24px] bg-gradient-to-b from-black/10 via-black/5 to-transparent p-[1px]">
							<div className="relative flex h-full flex-col overflow-hidden rounded-[15px] sm:rounded-[19px] md:rounded-[23px] border border-black/10 bg-white/95 p-4 sm:p-5 md:p-6 lg:p-7 shadow-[0_10px_35px_-18px_rgba(0,0,0,0.35)] backdrop-blur-lg">
								<div className="pointer-events-none absolute -right-8 -top-8 sm:-right-10 sm:-top-10 h-20 w-20 sm:h-28 sm:w-28 rounded-full bg-primary/10 blur-2xl transition-all duration-500 group-hover:scale-125 group-hover:bg-primary/20" />
								<div className="mb-4 sm:mb-5 flex items-start justify-between gap-3 sm:gap-4">
									<motion.div
										whileHover={
											shouldReduceMotion ? {} : { rotate: 6, scale: 1.1 }
										}
										transition={{ duration: 0.25, ease: "easeOut" }}
										className="relative inline-flex h-10 sm:h-12 md:h-14 w-10 sm:w-12 md:w-14 items-center justify-center rounded-lg sm:rounded-xl md:rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-sm flex-shrink-0">
										<div className="w-5 h-5 sm:w-6 sm:h-6">
											{Icons[feature.icon]}
										</div>
									</motion.div>
									<span className="text-[10px] sm:text-xs font-semibold tracking-[0.18em] text-foreground/45 mt-1">{`0${index + 1}`}</span>
								</div>

								<h3 className="mb-2 text-base sm:text-lg md:text-xl font-semibold leading-tight text-foreground">
									{feature.title}
								</h3>
								<p className="text-xs sm:text-sm md:text-[15px] leading-relaxed text-muted-foreground">
									{feature.description}
								</p>

								<div className="mt-4 sm:mt-5 md:mt-6 h-[2px] w-full overflow-hidden rounded-full bg-black/10">
									<div className="h-full w-1/3 origin-left rounded-full bg-primary transition-transform duration-500 ease-out group-hover:scale-x-[2.4]" />
								</div>
							</div>
						</motion.article>
					))}
				</motion.div>
			</div>
		</motion.section>
	);
}
